/**
 * Single source of truth for the visitor's cookie/analytics consent choice (#125).
 *
 * The banner, the footer "cookie preferences" entry, Google Consent Mode and the
 * first-party page tracking all read the same state from here, so a choice (or a
 * withdrawal) is applied everywhere at once, including other open tabs.
 *
 * Fail-closed contract: optional analytics is only ever "allowed" after an explicit,
 * current-version `accepted` choice. Unset, rejected, invalid/old-version, corrupt or
 * unreadable storage all resolve to "not allowed". Storage writes can throw (private
 * mode, quota, blocked site data); that never propagates to the caller.
 */

export const CONSENT_STORAGE_KEY = 'mbbe_cookie_consent';
export const CONSENT_VERSION = 1;
/**
 * Body field the API requires before it persists an optional analytics event. It is carried
 * in the JSON body (not a custom header) so no CORS preflight allow-list change is needed.
 */
export const ANALYTICS_CONSENT_FIELD = 'analyticsConsent';
export const ANALYTICS_CONSENT_GRANTED = 'granted';

export type ConsentChoice = 'accepted' | 'rejected';
/** `unset` covers: never chosen, invalid JSON/shape, and a stale consent version. */
export type ConsentState = ConsentChoice | 'unset';

type StoredConsent = {
  choice: ConsentChoice;
  version: number;
  updatedAt: string;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer: Record<string, unknown>[];
  }
}

type Listener = () => void;

const listeners = new Set<Listener>();
// Holds a choice that could not be persisted so the current page lifetime still honours
// what the visitor clicked (including a withdrawal over a previously stored "accepted").
// It never outlives the page; a successful write or another tab's change clears it.
let memoryChoice: ConsentChoice | null = null;
let preferencesOpen = false;

function parseStored(raw: string | null): ConsentChoice | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (parsed.choice !== 'accepted' && parsed.choice !== 'rejected') return null;
    return parsed.choice;
  } catch {
    return null;
  }
}

function readStoredChoice(): ConsentChoice | null {
  try {
    return parseStored(localStorage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function getConsentState(): ConsentState {
  return memoryChoice ?? readStoredChoice() ?? 'unset';
}

/** True only for an explicit, current accepted choice. Everything else is "no". */
export function hasAnalyticsConsent(): boolean {
  return getConsentState() === 'accepted';
}

function notify(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A subscriber must never break the choice handler.
    }
  }
}

export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Mirror the choice into Google Consent Mode v2 (defaults are `denied` in index.html). */
export function applyGoogleConsent(choice: ConsentChoice): void {
  const consentValue = choice === 'accepted' ? 'granted' : 'denied';
  const flags = {
    ad_storage: consentValue,
    analytics_storage: consentValue,
    ad_user_data: consentValue,
    ad_personalization: consentValue,
  };
  try {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', flags);
      return;
    }
    window.dataLayer.push({ event: 'consent_update', ...flags });
  } catch {
    // Consent signalling is best effort; the stored choice is authoritative.
  }
}

/**
 * Record a choice. Persists when possible, always updates in-memory state, applies the
 * Google flags and notifies subscribers. Never throws.
 */
export function setConsentChoice(choice: ConsentChoice): void {
  const payload: StoredConsent = {
    choice,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
    memoryChoice = null;
  } catch {
    memoryChoice = choice;
  }
  preferencesOpen = false;
  applyGoogleConsent(choice);
  notify();
}

/** Re-apply a previously stored choice to Google Consent Mode on page load. */
export function syncStoredConsent(): ConsentState {
  const state = getConsentState();
  if (state !== 'unset') applyGoogleConsent(state);
  return state;
}

export function openConsentPreferences(): void {
  preferencesOpen = true;
  notify();
}

export function closeConsentPreferences(): void {
  if (!preferencesOpen) return;
  preferencesOpen = false;
  notify();
}

export function isConsentPreferencesOpen(): boolean {
  return preferencesOpen;
}

/** Test helper: reset module state between tests. */
export function resetConsentForTests(): void {
  memoryChoice = null;
  preferencesOpen = false;
  listeners.clear();
}

// Another tab changed (or cleared) the choice: re-read and notify, so a withdrawal in one
// tab stops tracking in every tab.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== CONSENT_STORAGE_KEY) return;
    // Another tab wrote (or cleared) the choice after any unpersisted local one.
    memoryChoice = null;
    const state = getConsentState();
    // A cleared/invalid value means "no choice": Google flags go back to denied.
    applyGoogleConsent(state === 'accepted' ? 'accepted' : 'rejected');
    notify();
  });
}
